/**
 * Notification Service - Enterprise-grade notification management
 * 
 * Features:
 * - Automatic subscription/unsubscription
 * - Offline notification queuing
 * - Duplicate detection
 * - Pull + Push hybrid architecture
 * - Graceful degradation
 * 
 * @author Senior Developer
 * @version 2.0.0
 */

import webSocketManager, { ConnectionState } from '@/shared/services/WebSocketManager';
import { notificationStore } from '@/domains/notifications/store/notificationStore';
import { useLiveStore } from '@/domains/live/store/liveStore';
import { hasValidAccessToken } from '@/shared/utils/authSession.util';
import { ConnectionErrorCodes } from '@/shared/services/socket/constants';
import { getAuthStoreSafe } from '@/shared/utils/piniaSafe.util';
import { getSocketServiceInstance } from '@/shared/services/socket/SocketService';

const FATAL_AUTH_ERROR_CODES = new Set([
  ConnectionErrorCodes.WS_TOKEN_INVALID,
  ConnectionErrorCodes.WS_TOKEN_REFRESH_TIMEOUT,
  ConnectionErrorCodes.WS_SESSION_INVALID,
  ConnectionErrorCodes.WS_USER_NOT_FOUND,
  ConnectionErrorCodes.WS_USER_INACTIVE,
  ConnectionErrorCodes.WS_AUTH_FAILED,
  ConnectionErrorCodes.WS_SESSION_EXPIRED,
]);

// ============================================================================
// Configuration
// ============================================================================

const CONFIG = {
  SUBSCRIPTION: {
    CATEGORIES: ['all'],
    AUTO_SUBSCRIBE: true,
    AUTO_RESUBSCRIBE: true,
    RETRY_DELAY: 3000,
    MAX_RETRIES: 5,
  },
  
  SYNC: {
    ON_CONNECT: true,
    ON_RECONNECT: true,
    DEBOUNCE_DELAY: 2000,
  },

  DEDUPLICATION: {
    ENABLED: true,
    WINDOW: 1000, // 1 second
  },

  LOGGING: {
    ENABLED: process.env.NODE_ENV === 'development',
    VERBOSE: false,
  },
};

// ============================================================================
// Notification Service Class
// ============================================================================

class NotificationService {
  constructor() {
    this._store = null; // Lazy initialization
    this.storeInitialized = false;
    this.isInitialized = false;
    this.isSubscribed = false;
    this.subscriptionRetries = 0;
    this.deduplicationCache = new Map();
    this.syncDebounceTimer = null;
    this.isAuthenticated = false; // Track authentication state
    this.connectionSuccessHandler = null; // Store handler reference for cleanup
    this.handleNotificationDeleted = null;
    this.handleSessionExpired = null;
    this.handleConnectionError = null;
    this.handleAuthError = null;
    this.handleSessionReplaced = null;
    this.handleUserRestricted = null;
    this.handleUserUnrestricted = null;
    this.stateChangeUnsub = null;
    this._handlingFatalAuth = false;
    this._initialSyncDone = false;
  }

  /**
   * Get store instance (lazy initialization)
   * This ensures Pinia is initialized before accessing the store
   */
  get store() {
    if (!this._store) {
      try {
        this._store = notificationStore();
        this.storeInitialized = true;
      } catch (error) {
        console.error('[NotificationService] Failed to initialize store:', error.message);
        throw new Error('Pinia store not available. Make sure app.use(pinia) is called before initializing notifications.');
      }
    }
    return this._store;
  }

  // ==========================================================================
  // Public API
  // ==========================================================================

  /**
   * Initialize the notification system
   */
  async initialize() {
    if (this.isInitialized) {
      this.log('Already initialized');
      return;
    }

    this.log('Initializing notification system...');

    try {
      // 1. Setup WebSocket state listeners
      this.setupWebSocketListeners();

      // 2. Setup notification event listeners
      this.setupNotificationListeners();

      // 3. Initial sync if already connected
      if (webSocketManager.isConnected()) {
        await this.sync();
        this._initialSyncDone = true;
        // ✅ FIX: Only subscribe if authenticated (connectionSuccess already received)
        // Otherwise, wait for connectionSuccess event or try after a short delay
        if (this.isAuthenticated) {
          this.subscribe();
        } else {
          this.log('⏳ Waiting for authentication to complete before subscribing...');
          // Fallback: We may have missed connectionSuccess (e.g. listener registered after it fired)
          setTimeout(() => {
            if (webSocketManager.isConnected() && !this.isSubscribed) {
              if (!this.isAuthenticated) {
                this.isAuthenticated = true;
                this.log('🔄 Assuming authenticated (connectionSuccess may have fired before listener).');
              }
              this.subscribe();
            }
          }, 1000);
        }
      }

      this.isInitialized = true;
      this.log('✅ Notification system initialized');
    } catch (error) {
      this.logError('Failed to initialize notification system:', error);
      throw error;
    }
  }

  /**
   * Clean up the notification system
   */
  cleanup() {
    this.log('Cleaning up notification system...');

    // Unsubscribe from notifications
    this.unsubscribe();

    // Clear sync timer
    if (this.syncDebounceTimer) {
      clearTimeout(this.syncDebounceTimer);
      this.syncDebounceTimer = null;
    }

    // Remove listeners
    if (this.handleNotificationCreated) {
      webSocketManager.off('notification_created', this.handleNotificationCreated);
    }
    if (this.handleNotificationUpdated) {
      webSocketManager.off('notification_updated', this.handleNotificationUpdated);
    }
    if (this.handleNotificationDeleted) {
      webSocketManager.off('notification_deleted', this.handleNotificationDeleted);
    }
    if (this.handleSessionExpired) {
      webSocketManager.off('sessionExpired', this.handleSessionExpired);
    }
    if (this.handleConnectionError) {
      webSocketManager.off('connectionError', this.handleConnectionError);
    }
    if (this.handleAuthError) {
      webSocketManager.off('auth_error', this.handleAuthError);
    }
    if (this.handleSessionReplaced) {
      webSocketManager.off('session:replaced', this.handleSessionReplaced);
    }
    if (this.handleUserRestricted) {
      webSocketManager.off('user:restricted', this.handleUserRestricted);
    }
    if (this.handleUserUnrestricted) {
      webSocketManager.off('user:unrestricted', this.handleUserUnrestricted);
    }
    
    // ✅ FIX: Remove connectionSuccess listener
    if (this.connectionSuccessHandler) {
      webSocketManager.off('connectionSuccess', this.connectionSuccessHandler);
      this.connectionSuccessHandler = null;
    }
    if (this.stateChangeUnsub) {
      this.stateChangeUnsub();
      this.stateChangeUnsub = null;
    }

    // Clear store
    this.store.clearNotifications();

    // Reset state
    this.isInitialized = false;
    this.isSubscribed = false;
    this.subscriptionRetries = 0;
    this.isAuthenticated = false;
    this._initialSyncDone = false;

    this.log('✅ Notification system cleaned up');
  }

  /**
   * Subscribe to notification categories
   */
  async subscribe(categories = CONFIG.SUBSCRIPTION.CATEGORIES) {
    if (!webSocketManager.isConnected()) {
      this.log('Cannot subscribe - socket not connected');
      return false;
    }

    // ✅ FIX: Check if authentication is complete before subscribing
    if (!this.isAuthenticated) {
      const error = new Error('Authentication not yet complete. Please wait and retry.');
      this.logError('❌ Subscription failed:', error);
      this.handleSubscriptionError(categories, true); // Mark as retryable
      return false;
    }

    if (this.isSubscribed) {
      this.log('Already subscribed to notifications');
      return true;
    }

    try {
      this.log(`Subscribing to categories: ${categories.join(', ')}...`);

      const response = await webSocketManager.emit(
        'subscribeToNotifications',
        { categories },
        {
          timeout: 10000,
        }
      );

      if (response?.success) {
        this.isSubscribed = true;
        this.subscriptionRetries = 0;
        this.log(`✅ Successfully subscribed to: ${response.categories?.join(', ') || 'all'}`);
        return true;
      } else {
        // Check if error indicates authentication not complete
        const isAuthError = response?.error?.includes('Authentication not yet complete') || 
                           response?.retryable === true;
        
        if (isAuthError) {
          this.log('⏳ Authentication not yet complete, will retry...');
          this.handleSubscriptionError(categories, true); // Mark as retryable
          return false;
        }
        
        throw new Error(response?.error || 'Subscription failed');
      }
    } catch (error) {
      // Check if error message indicates authentication not complete
      const isAuthError = error?.message?.includes('Authentication not yet complete');
      
      if (isAuthError) {
        this.log('⏳ Authentication not yet complete, will retry...');
        this.handleSubscriptionError(categories, true); // Mark as retryable
      } else {
        this.logError('❌ Subscription failed:', error);
        this.handleSubscriptionError(categories, false);
      }
      return false;
    }
  }

  /**
   * Unsubscribe from notifications
   */
  async unsubscribe() {
    if (!this.isSubscribed) {
      return;
    }

    try {
      if (webSocketManager.isConnected()) {
        await webSocketManager.emit('unsubscribeFromNotifications', {}, {
          timeout: 5000,
        });
      }
      
      this.isSubscribed = false;
      this.log('✅ Unsubscribed from notifications');
    } catch (error) {
      this.logError('Failed to unsubscribe:', error);
      // Force unsubscribe even if server fails
      this.isSubscribed = false;
    }
  }

  /**
   * Sync notifications from server (pull mechanism)
   */
  async sync() {
    this.log('Syncing notifications...');

    try {
      // Fetch latest notifications (fetchNotifications already refreshes unread count)
      await this.store.fetchNotifications(0, 10, true, false);
      
      this.log('✅ Notifications synced');
    } catch (error) {
      this.logError('Failed to sync notifications:', error);
    }
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId) {
    try {
      await this.store.markAsRead(notificationId);
      this.log(`✅ Marked notification as read: ${notificationId}`);
    } catch (error) {
      this.logError('Failed to mark notification as read:', error);
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead() {
    try {
      await this.store.markAllAsRead();
      this.log('✅ Marked all notifications as read');
    } catch (error) {
      this.logError('Failed to mark all notifications as read:', error);
    }
  }

  /**
   * Load more notifications
   */
  async loadMore() {
    try {
      await this.store.loadMore();
      this.log('✅ Loaded more notifications');
    } catch (error) {
      this.logError('Failed to load more notifications:', error);
    }
  }

  /**
   * Refresh notifications
   */
  async refresh() {
    try {
      await this.store.refresh();
      this.log('✅ Notifications refreshed');
    } catch (error) {
      this.logError('Failed to refresh notifications:', error);
    }
  }

  /**
   * Get current notification state
   */
  getState() {
    return {
      isInitialized: this.isInitialized,
      isSubscribed: this.isSubscribed,
      socketState: webSocketManager.getState(),
      unreadCount: this.store.unreadCount.value,
      totalNotifications: this.store.notifications.value?.length || 0,
    };
  }

  // ==========================================================================
  // Private Methods
  // ==========================================================================

  /**
   * Bind a class method for use as a stable WebSocket listener reference.
   * Uses the prototype method so constructor `= null` placeholders do not shadow it.
   */
  _bindInstanceMethod(methodName) {
    const fn = this.constructor.prototype[methodName];
    if (typeof fn !== 'function') {
      throw new Error(`[NotificationService] Missing handler: ${methodName}`);
    }
    return fn.bind(this);
  }

  setupWebSocketListeners() {
    if (this.stateChangeUnsub) {
      this.stateChangeUnsub();
      this.stateChangeUnsub = null;
    }
    this.stateChangeUnsub = webSocketManager.onStateChange((newState, oldState) => {
      this.handleStateChange(newState, oldState);
    });

    // ✅ FIX: Listen for connectionSuccess event to know when authentication is complete
    this.connectionSuccessHandler = () => {
      this.isAuthenticated = true;
      this.log('✅ Authentication complete - ready to subscribe');
      
      // Subscribe if auto-subscribe is enabled and not already subscribed
      if (CONFIG.SUBSCRIPTION.AUTO_SUBSCRIBE && !this.isSubscribed && webSocketManager.isConnected()) {
        this.subscribe();
      }
    };
    
    webSocketManager.on('connectionSuccess', this.connectionSuccessHandler);

    this.handleSessionExpired = this._bindInstanceMethod('handleSessionExpired');
    this.handleConnectionError = this._bindInstanceMethod('handleConnectionError');
    this.handleAuthError = this._bindInstanceMethod('handleAuthError');
    this.handleSessionReplaced = this._bindInstanceMethod('handleSessionReplaced');
    this.handleUserRestricted = this._bindInstanceMethod('handleUserRestricted');
    this.handleUserUnrestricted = this._bindInstanceMethod('handleUserUnrestricted');
    webSocketManager.on('sessionExpired', this.handleSessionExpired);
    webSocketManager.on('connectionError', this.handleConnectionError);
    webSocketManager.on('auth_error', this.handleAuthError);
    webSocketManager.on('session:replaced', this.handleSessionReplaced);
    webSocketManager.on('user:restricted', this.handleUserRestricted);
    webSocketManager.on('user:unrestricted', this.handleUserUnrestricted);
  }

  setupNotificationListeners() {
    if (this.handleNotificationCreated) {
      webSocketManager.off('notification_created', this.handleNotificationCreated);
    }
    if (this.handleNotificationUpdated) {
      webSocketManager.off('notification_updated', this.handleNotificationUpdated);
    }
    if (this.handleNotificationDeleted) {
      webSocketManager.off('notification_deleted', this.handleNotificationDeleted);
    }

    this.handleNotificationCreated = this._bindInstanceMethod('handleNotificationCreated');
    this.handleNotificationUpdated = this._bindInstanceMethod('handleNotificationUpdated');
    this.handleNotificationDeleted = this._bindInstanceMethod('handleNotificationDeleted');

    webSocketManager.on('notification_created', this.handleNotificationCreated);
    webSocketManager.on('notification_updated', this.handleNotificationUpdated);
    webSocketManager.on('notification_deleted', this.handleNotificationDeleted);
  }

  handleStateChange(newState, oldState) {
    this.log(`State changed: ${oldState} → ${newState}`);

    switch (newState) {
      case ConnectionState.CONNECTED:
        this.handleConnected();
        break;

      case ConnectionState.RECONNECTING:
        this.handleReconnecting();
        break;

      case ConnectionState.DISCONNECTED:
        this.handleDisconnected();
        break;
    }
  }

  async handleConnected() {
    this.log('WebSocket connected - performing post-connection tasks...');
    
    // Reset authentication state - wait for connectionSuccess event
    this.isAuthenticated = false;

    // Pull sync on reconnect only — initialize() already syncs on first load.
    if (CONFIG.SYNC.ON_CONNECT && !this._initialSyncDone) {
      this.debouncedSync();
    }

    // ✅ FIX: Don't subscribe immediately - wait for connectionSuccess event
    // The connectionSuccess event indicates authentication is complete
    // If already authenticated (e.g., reconnection), subscribe immediately
    if (CONFIG.SUBSCRIPTION.AUTO_SUBSCRIBE && this.isAuthenticated) {
      await this.subscribe();
    } else {
      this.log('⏳ Waiting for authentication to complete before subscribing...');
    }
  }

  handleReconnecting() {
    this.log('WebSocket reconnecting...');
    this.isSubscribed = false;
    this.isAuthenticated = false; // Reset auth state on reconnect
  }

  handleDisconnected() {
    this.log('WebSocket disconnected');
    this.isSubscribed = false;
    this.isAuthenticated = false;
    this._initialSyncDone = false;
  }

  /**
   * Idempotent handler: upsert by notification.id (supports event replay after reconnect).
   */
  handleNotificationCreated(notification) {
    const authStore = getAuthStoreSafe();
    if (!authStore || authStore.isLoggedOut || authStore.shouldPreventApiCall) {
      return;
    }

    this.log('📨 Received notification_created event:', {
      id: notification?.id,
      type: notification?.type,
    });

    if (!notification?.id) {
      this.logError('⚠️ Received null/undefined or missing id');
      return;
    }

    if (this.isDuplicate(notification, 'created')) {
      this.log('⏭️ Duplicate notification_created skipped:', notification.id);
      return;
    }

    try {
      if (this.syncDebounceTimer) {
        clearTimeout(this.syncDebounceTimer);
        this.syncDebounceTimer = null;
      }
      this.store.cancelPendingUnreadRefresh?.();

      this.store.addNotification(notification);
      this.log('✅ Notification upserted:', notification.id, notification.type);

      if (notification.type === 'USER_WENT_LIVE' && hasValidAccessToken()) {
        try {
          const liveStore = useLiveStore();
          liveStore.setupSocketListeners();
          liveStore.fetchFollowingLive();
        } catch { /* live feed refresh is best-effort */ }
      }

      if (notification.eventId && webSocketManager.isConnected()) {
        webSocketManager.emit('eventAcknowledged', {
          eventId: notification.eventId,
          eventType: 'notification_created',
          timestamp: Date.now(),
        }, {
          timeout: 5000,
        }).catch(error => {
          this.logError('Failed to acknowledge notification:', error);
        });
      }
    } catch (error) {
      this.logError('❌ Failed to add notification to store:', error);
    }
  }

  /**
   * Idempotent handler: remove by notification id (supports event replay after reconnect).
   */
  handleNotificationDeleted(payload) {
    const authStore = getAuthStoreSafe();
    if (!authStore || authStore.isLoggedOut || authStore.shouldPreventApiCall) {
      return;
    }

    const id = typeof payload === 'string'
      ? payload
      : (payload?.id ?? payload?.notificationId);

    this.log('📨 Received notification_deleted event:', id);

    if (!id) return;

    try {
      const removed = this.store.removeNotificationById(id);
      if (removed) {
        this.log('✅ Notification removed locally:', id);
      }

      const eventId = typeof payload === 'object' ? payload?.eventId : null;
      if (eventId && webSocketManager.isConnected()) {
        webSocketManager.emit('eventAcknowledged', {
          eventId,
          eventType: 'notification_deleted',
          timestamp: Date.now(),
        }, {
          timeout: 5000,
        }).catch(error => {
          this.logError('Failed to acknowledge deletion:', error);
        });
      }
    } catch (error) {
      this.logError('❌ Failed to remove notification:', error);
    }
  }

  handleSessionExpired(payload) {
    this.log('Session expired via WebSocket');
    const message = payload?.message || 'Your session has expired. Please sign in again.';
    this.performFatalAuthLogout(message);
  }

  handleConnectionError(payload) {
    const code = payload?.code;
    if (!code) return;

    if (code === ConnectionErrorCodes.WS_TOO_MANY_CONNECTIONS) {
      this.log('Too many connections — backing off before reconnect');
      const delay = payload?.retryAfterMs ?? 30000;
      getSocketServiceInstance().setReconnectDelay(delay);
      return;
    }

    if (!FATAL_AUTH_ERROR_CODES.has(code)) return;

    this.log(`Fatal connection error: ${code}`);
    this.performFatalAuthLogout(payload?.message || 'Please sign in again.');
  }

  handleAuthError(payload) {
    const code = payload?.code || payload?.error?.code;
    if (!code || !FATAL_AUTH_ERROR_CODES.has(code)) return;

    this.log(`Fatal auth_error: ${code}`);
    this.performFatalAuthLogout(payload?.message || payload?.error?.message || 'Please sign in again.');
  }

  handleSessionReplaced(payload) {
    const code = payload?.code;
    this.log('Session replaced event:', code, payload?.message);

    if (code === 'WS_SESSION_REPLACED' || code === 'WS_SESSION_EVICTED') {
      this.isSubscribed = false;
      this.isAuthenticated = false;
      return;
    }

    if (payload?.requiresReconnect === true) {
      this.isSubscribed = false;
      this.isAuthenticated = false;
      return;
    }

    this.performFatalAuthLogout(
      payload?.message || 'Your session was opened on another device. Please sign in again.'
    );
  }

  handleUserRestricted(payload) {
    const authStore = getAuthStoreSafe();
    if (!authStore || authStore.isLoggedOut) return;
    const reason = payload?.reason || 'Your account has been restricted.';
    this.performFatalAuthLogout(reason);
  }

  handleUserUnrestricted() {
    this.log('User unrestricted event received');
  }

  async performFatalAuthLogout(message) {
    if (this._handlingFatalAuth) return;

    const authStore = getAuthStoreSafe();
    if (!authStore || authStore.isLoggedOut) return;

    this._handlingFatalAuth = true;

    try {
      this.unsubscribe().catch(() => {});
      const { performSessionLogout } = await import('@/shared/utils/sessionLogout.util');
      await performSessionLogout({ securityMessage: message });
      this.isSubscribed = false;
      this.isAuthenticated = false;
    } catch (error) {
      this.logError('Fatal auth logout failed:', error);
    } finally {
      this._handlingFatalAuth = false;
    }
  }

  /**
   * Idempotent handler: upsert by notification.id (supports event replay after reconnect).
   */
  handleNotificationUpdated(notification) {
    const authStore = getAuthStoreSafe();
    if (!authStore || authStore.isLoggedOut || authStore.shouldPreventApiCall) {
      return;
    }

    this.log('📨 Received notification_updated event:', notification?.id);

    if (!notification?.id) return;

    if (this.isDuplicate(notification, 'updated')) {
      this.log('⏭️ Duplicate notification_updated skipped:', notification.id);
      return;
    }

    try {
      const notifications = this.store.notifications.value;
      if (!Array.isArray(notifications)) return;

      const index = notifications.findIndex(n => n.id === notification.id);
      if (index !== -1) {
        notifications[index] = notification;
        this.log('✅ Notification updated:', notification.id);
      } else {
        this.store.addNotification(notification);
        this.log('✅ Notification added (from updated):', notification.id);
      }

      if (notification.eventId && webSocketManager.isConnected()) {
        webSocketManager.emit('eventAcknowledged', {
          eventId: notification.eventId,
          eventType: 'notification_updated',
          timestamp: Date.now(),
        }, {
          timeout: 5000,
        }).catch(error => {
          this.logError('Failed to acknowledge update:', error);
        });
      }
    } catch (error) {
      this.logError('❌ Failed to update notification:', error);
    }
  }

  handleSubscriptionError(categories, isRetryable = false) {
    // Only increment retries for non-retryable errors or if we've already tried
    if (!isRetryable) {
      this.subscriptionRetries++;
    }

    if (this.subscriptionRetries >= CONFIG.SUBSCRIPTION.MAX_RETRIES) {
      this.logError(`❌ Maximum subscription retries (${CONFIG.SUBSCRIPTION.MAX_RETRIES}) reached`);
      this.subscriptionRetries = 0;
      return;
    }

    if (CONFIG.SUBSCRIPTION.AUTO_RESUBSCRIBE) {
      // For authentication errors, use shorter delay (auth should complete quickly)
      // For other errors, use exponential backoff
      const baseDelay = isRetryable ? 500 : 1000; // 500ms for auth errors, 1s for others
      const maxDelay = isRetryable ? 5000 : 30000; // 5s max for auth errors, 30s for others
      const backoffMultiplier = 2;
      const delay = Math.min(
        baseDelay * Math.pow(backoffMultiplier, this.subscriptionRetries - 1),
        maxDelay
      );

      this.log(`🔄 Retrying subscription in ${delay}ms (attempt ${this.subscriptionRetries + 1}/${CONFIG.SUBSCRIPTION.MAX_RETRIES})...`);
      
      setTimeout(() => {
        if (webSocketManager.isConnected()) {
          // If it's an auth error, check if authenticated before retrying
          if (isRetryable && !this.isAuthenticated) {
            this.log('⏳ Still waiting for authentication...');
            // Retry with shorter delay
            this.handleSubscriptionError(categories, true);
            return;
          }
          this.subscribe(categories);
        }
      }, delay);
    }
  }

  isDuplicate(notification, type = 'created') {
    if (!CONFIG.DEDUPLICATION.ENABLED) {
      return false;
    }

    const key = String(notification.id);
    const now = Date.now();
    const lastTime = this.deduplicationCache.get(key);

    if (lastTime && (now - lastTime) < CONFIG.DEDUPLICATION.WINDOW) {
      return true;
    }

    this.deduplicationCache.set(key, now);

    // Clean up old entries
    if (this.deduplicationCache.size > 100) {
      const cutoff = now - CONFIG.DEDUPLICATION.WINDOW;
      for (const [k, time] of this.deduplicationCache) {
        if (time < cutoff) {
          this.deduplicationCache.delete(k);
        }
      }
    }

    return false;
  }

  debouncedSync() {
    if (this.syncDebounceTimer) {
      clearTimeout(this.syncDebounceTimer);
    }

    this.syncDebounceTimer = setTimeout(() => {
      this.sync();
      this.syncDebounceTimer = null;
    }, CONFIG.SYNC.DEBOUNCE_DELAY);
  }

  log(...args) {
    if (CONFIG.LOGGING.ENABLED) {
      console.log('[NotificationService]', ...args);
    }
  }

  logError(...args) {
    console.error('[NotificationService]', ...args);
  }
}

// ============================================================================
// Singleton Instance (Lazy Initialization with Proxy)
// ============================================================================

let notificationServiceInstance = null;

/**
 * Get the singleton instance of NotificationService
 * Creates instance on first access to ensure Pinia is initialized
 */
function getInstance() {
  if (!notificationServiceInstance) {
    notificationServiceInstance = new NotificationService();
  }
  return notificationServiceInstance;
}

/**
 * Proxy that lazily initializes the service on first property access
 * This ensures Pinia is ready before the service is used
 */
const notificationServiceProxy = new Proxy({}, {
  get(target, prop) {
    const instance = getInstance();
    const value = instance[prop];
    
    // Bind methods to the instance
    if (typeof value === 'function') {
      return value.bind(instance);
    }
    
    return value;
  },
  
  set(target, prop, value) {
    const instance = getInstance();
    instance[prop] = value;
    return true;
  }
});

// ============================================================================
// Exports
// ============================================================================

export default notificationServiceProxy;

export {
  CONFIG as NotificationServiceConfig,
};
